import React, { useState } from 'react';
import { Home, Zap, Clock, User, MoreHorizontal, X } from 'lucide-react';
import { useOptimizerNav } from './OptimizerNavContext';
import type { DashboardPage } from './DashboardContainerStub';

const MORE_ITEMS: { id: DashboardPage; label: string; Icon: React.FC<{ size?: number; className?: string }> }[] = [
  { id: 'dashboard', label: 'Home', Icon: Home },
  { id: 'single-optimize', label: 'Optimizer', Icon: Zap },
  { id: 'history', label: 'Activities', Icon: Clock },
  { id: 'profile', label: 'Me', Icon: User },
];

interface OptimizerMoreMenuProps {
  /** Optional class for the button container */
  className?: string;
  /** Optional style for the button (e.g. to match step bar) */
  buttonStyle?: React.CSSProperties;
}

/**
 * A "More" button that opens a bottom sheet with Home, Optimizer, Activities, Me.
 * Use in optimizer step bottom bars when the main tab bar is hidden (native).
 */
export const OptimizerMoreMenu: React.FC<OptimizerMoreMenuProps> = ({ className, buttonStyle }) => {
  const nav = useOptimizerNav();
  const [open, setOpen] = useState(false);

  if (!nav) return null;

  const handleSelect = (page: DashboardPage) => {
    setOpen(false);
    nav.onNavigate(page);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight: 44,
          padding: '6px 12px',
          border: 'none',
          background: 'transparent',
          color: 'var(--ggj-tab-inactive, var(--vscode-text-tertiary))',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          ...buttonStyle,
        }}
        aria-label="More navigation"
      >
        <MoreHorizontal size={22} />
        <span style={{ fontSize: 10, fontWeight: 600 }}>More</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
            }}
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              background: 'var(--vscode-bg-elevated, #1e1e2e)',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.25)',
              paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid var(--vscode-border-primary, rgba(255,255,255,0.1))',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--vscode-text-primary)' }}>Navigation</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'var(--vscode-bg-tertiary)',
                  borderRadius: 10,
                  color: 'var(--vscode-text-secondary)',
                  cursor: 'pointer',
                }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0' }}>
              {MORE_ITEMS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelect(id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 20px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--vscode-text-primary)',
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Icon size={22} style={{ color: 'var(--vscode-primary)' }} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
